import type {
  AbstractPowerSyncDatabase,
  PowerSyncBackendConnector,
  PowerSyncConnectionOptions,
} from "@powersync/common";

/**
 * Throttle CRUD uploads modestly during initial sync to batch them away from
 * the heavy download stream, WITHOUT starving them.
 *
 * History: this was 30_000ms ("upload almost nothing until the initial download
 * settles"). That backfired badly for multi-account / fresh logins: the very
 * first thing a freshly-primed account did was seed a local `user` settings row
 * (a pending CRUD op). Since the seed guard in `theme-provider.tsx`
 * (`uid !== VISITOR_ID`), only a freshly-primed GUEST still does this — a real
 * account's `user` row comes from the server (`packages/core-rust/src/db/user.rs`)
 * and is no longer seeded locally, so it no longer forces this same pending op.
 * The guest path still does, so the short throttle below is still needed.
 * PowerSync will not finalize its checkpoint — and thus
 * never flips `hasSynced` to `true` — while a local write is still pending
 * upload. With a 30s throttle the checkpoint (and the whole UI, which gates the
 * "wake up watched queries" on `hasSynced`) stayed frozen for ~30 seconds after
 * login: newly-added records didn't render, sync looked stuck. A short throttle
 * lets the pending write upload promptly so the checkpoint completes within a
 * second or two, while still coalescing bursts during the download.
 */
const INITIAL_SYNC_CRUD_THROTTLE_MS = 1000;

export interface PowerSyncInitOptions {
  isWeb: boolean;
  isRetry: boolean;
  isIndexedDBError: (error: unknown) => boolean;
  clearLocalDatabase: () => Promise<void>;
  onStatusUpdate: (update: Partial<{ isLoading: boolean; dbError: string | null }>) => void;
  retry: (isRetry: boolean) => Promise<void>;
  i18n: {
    browserError: string;
    chromeHelp: string;
  };
}

/**
 * Agnostic logic for PowerSync initialization and error handling.
 */
export async function handlePowerSyncInitError(error: unknown, options: PowerSyncInitOptions) {
  const { isWeb, isRetry, isIndexedDBError, clearLocalDatabase, onStatusUpdate, retry, i18n } =
    options;

  console.error("[PowerSync] Init error:", error);

  if (isWeb && !isRetry && isIndexedDBError(error)) {
    try {
      await clearLocalDatabase();
      const RETRY_DELAY_MS = 500;
      await new Promise<void>((resolve) => {
        setTimeout(resolve, RETRY_DELAY_MS);
      });
      await retry(true);
      return;
    } catch (cleanupError) {
      console.error("[PowerSync] Failed to cleanup IndexedDB:", cleanupError);
    }
  }

  // Final error state
  onStatusUpdate({ isLoading: false });

  if (isWeb && isIndexedDBError(error)) {
    onStatusUpdate({
      dbError: `${i18n.browserError}\n${i18n.chromeHelp}`,
    });
  }
}

export interface AutoConnectOptions {
  database: AbstractPowerSyncDatabase;
  connector: PowerSyncBackendConnector;
  isAuthenticated: boolean;
  isVisitor?: boolean;
  isPriming?: boolean;
  isConnecting: boolean;
  setIsConnecting: (val: boolean) => void;
  onStatusUpdate?: (update: { isConnected: boolean }) => void;
  /**
   * Server maintenance window with a `sync` (or `all`) scope: PowerSync must
   * stay disconnected for its duration.
   *
   * Proactive on purpose. The server's 503 on the write path is only the safety
   * net for clients that never saw the window; the client is supposed to stop
   * hammering by itself and reconnect when the window lifts. Because the health
   * poll keeps running while disconnected, the caller re-runs this with `false`
   * as soon as the server is back and the connection is restored WITHOUT any
   * user action.
   *
   * `writes`-scoped windows must NOT set this: downloads keep flowing and only
   * uploads pause (see `ConnectorConfig.canUploadNow`).
   */
  isMaintenancePaused?: boolean;
}

/**
 * Agnostic logic to handle PowerSync auto-connect/disconnect based on session state.
 * Consolidates the connection state machine used by both Web and Native apps.
 */
export async function handlePowerSyncAutoConnect(options: AutoConnectOptions) {
  const {
    database,
    connector,
    isAuthenticated,
    isVisitor = false,
    isPriming = false,
    isConnecting,
    setIsConnecting,
    onStatusUpdate,
    isMaintenancePaused = false,
  } = options;

  if (isPriming) {
    return;
  }

  // Guard: server maintenance with a `sync`/`all` scope — disconnect and stay
  // out until it lifts. Same shape as the visitor guard below.
  if (isMaintenancePaused) {
    if (database.connected || isConnecting) {
      console.warn("[PowerSync] Server maintenance window. Disconnecting until it lifts.");
      setIsConnecting(false);
      try {
        await database.disconnect();
        onStatusUpdate?.({ isConnected: false });
      } catch (err) {
        console.error("[PowerSync] Disconnection failed:", err);
      }
    }
    return;
  }

  // Guard: If visitor or no auth, ensure disconnected
  if (isVisitor || !isAuthenticated) {
    if (database.connected || isConnecting) {
      console.warn("[PowerSync] Visitor or no auth detected. Forcing disconnection.");
      setIsConnecting(false);
      try {
        await database.disconnect();
        onStatusUpdate?.({ isConnected: false });
      } catch (err) {
        console.error("[PowerSync] Disconnection failed:", err);
      }
    }
    return;
  }

  // If authenticated, attempt connection
  if (!database.connected && !isConnecting) {
    console.log("[PowerSync] Auto-connecting...");
    setIsConnecting(true);
    try {
      // During initial sync, throttle CRUD uploads to avoid adding bridge traffic
      // on top of the already-heavy download stream. After hasSynced=true the
      // default throttle (~1s) takes over automatically on reconnect.
      const connectOptions: PowerSyncConnectionOptions = database.currentStatus.hasSynced
        ? {}
        : { crudUploadThrottleMs: INITIAL_SYNC_CRUD_THROTTLE_MS };
      await database.connect(connector, connectOptions);
      onStatusUpdate?.({ isConnected: true });
    } catch (err) {
      console.error("[PowerSync] Auto-connection failed:", err);
    } finally {
      setIsConnecting(false);
    }
  }
}
